import React, { useState, useEffect, useMemo } from "react";
import { Modal, Select, Checkbox, Avatar, Tag, Spin, Empty, Button, Alert, Image, Rate, Tabs, Input, Tooltip } from "antd";
import { SendOutlined, CarOutlined, ToolOutlined, SearchOutlined, GlobalOutlined, MessageOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_DOMAIN } from "../constants";
import { getAgentColor } from "./helpers";
import { useSupplierMessageComposer, SupplierSelectionPanel, SupplierPreviewPanel } from "./SupplierMessageComposer";

let _cachedGroups = null;
let _groupsFetch = null;

export function preloadSupplierGroups() { return fetchGroupsOnce(); }

function fetchGroupsOnce() {
  if (_cachedGroups) return Promise.resolve(_cachedGroups);
  if (_groupsFetch) return _groupsFetch;
  _groupsFetch = axios
    .post(`${API_DOMAIN}/whatsapp/get_supplier_tags`, {}, {
      headers: { accessToken: localStorage.getItem("accessToken") },
    })
    .then(res => {
      _cachedGroups = res.data.success
        ? (res.data.data || []).map(g => ({ value: g.id, label: g.name }))
        : [];
      return _cachedGroups;
    })
    .catch(() => { _groupsFetch = null; return []; });
  return _groupsFetch;
}

function supplierInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : name[0].toUpperCase();
}

// `selectedIndices`/`onToggle` let staff exclude photos from this
// particular supplier message — e.g. a follow-up send after adding a new
// part shouldn't drag along photos suppliers already saw on the first send.
// Selection is keyed by array index (a stable `id` per image isn't
// guaranteed to exist on every entry yet — see docs/FUTURE_TASKS.md,
// "Selective photo send on send_to_suppliers_v2").
function QueryDetailsBanner({ task, selectedIndices, onToggle }) {
  if (!task) return null;
  const v = task.vehicle;
  const vehicleLabel = v
    ? [v.brand_name || v.brand, v.model_name || v.model, v.year ? `(${v.year})` : ""].filter(Boolean).join(" ")
    : null;
  const parts = (task.query_parts || []).map(p => p.part_name).filter(Boolean);
  const images = Array.isArray(task.images) ? task.images : [];

  return (
    <div style={{
      background: "#f8f9fb", border: "1px solid #e8e8e8", borderRadius: 8,
      padding: "10px 12px", marginBottom: 14, display: "flex", flexDirection: "column", gap: 8,
    }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        {vehicleLabel && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600 }}>
            <CarOutlined style={{ color: "#1677ff", fontSize: 13 }} />
            <span>{vehicleLabel}</span>
          </div>
        )}
        {parts.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
            <ToolOutlined style={{ color: "#8c8c8c", fontSize: 11 }} />
            {parts.map((p, i) => (
              <Tag key={i} style={{ margin: 0, fontSize: 10, padding: "0 5px", lineHeight: "18px" }}>{p}</Tag>
            ))}
          </div>
        )}
      </div>

      {images.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: "#8c8c8c" }}>
            Photos to include ({selectedIndices?.size ?? images.length}/{images.length})
          </div>
          <Image.PreviewGroup>
            <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 2 }}>
              {images.map((src, i) => {
                const url = typeof src === "string"
                  ? (src.startsWith("http") ? src : `${API_DOMAIN}${src}`)
                  : src?.url || src?.data || "";
                const checked = selectedIndices?.has(i) ?? true;
                return (
                  <div key={i} style={{ position: "relative", flexShrink: 0 }}>
                    <Image
                      src={url}
                      width={64}
                      height={64}
                      style={{ objectFit: "cover", borderRadius: 6, border: "1px solid #e8e8e8", cursor: "pointer", opacity: checked ? 1 : 0.4 }}
                      preview={{ src: url }}
                    />
                    {onToggle && (
                      <Checkbox
                        checked={checked}
                        onChange={() => onToggle(i)}
                        onClick={e => e.stopPropagation()}
                        style={{ position: "absolute", top: 2, left: 2, background: "#fff", borderRadius: 4, padding: 1 }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </Image.PreviewGroup>
        </>
      )}
    </div>
  );
}

function SupplierRow({ s, checkedIds, onToggle, selectedGroups, previouslyContactedIds }) {
  const matchedTags = (s.tag || []).filter(t => selectedGroups.includes(t.value));
  const wasSent = previouslyContactedIds?.has(s.id);
  return (
    <div
      onClick={() => onToggle(s.id)}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "7px 10px", borderRadius: 8, cursor: "pointer",
        background: checkedIds.has(s.id) ? "#e6f4ff" : "#fafafa",
        border: `1px solid ${checkedIds.has(s.id) ? "#91caff" : "#f0f0f0"}`,
        transition: "all 0.15s",
      }}
    >
      <Checkbox
        checked={checkedIds.has(s.id)}
        onChange={() => onToggle(s.id)}
        onClick={e => e.stopPropagation()}
      />
      <Avatar size={30} style={{ backgroundColor: getAgentColor(s.name), fontSize: 11, flexShrink: 0 }}>
        {supplierInitials(s.name)}
      </Avatar>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 5 }}>
          {s.name}
          {wasSent && <Tag color="orange" style={{ margin: 0, fontSize: 9, padding: "0 4px", lineHeight: "16px", flexShrink: 0 }}>Sent</Tag>}
        </div>
        {matchedTags.length > 0 && (
          <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginTop: 2 }}>
            {matchedTags.map(t => (
              <Tag key={t.value} style={{ margin: 0, fontSize: 9, padding: "0 4px", lineHeight: "16px" }}>
                {t.label}
              </Tag>
            ))}
          </div>
        )}
      </div>
      <Rate disabled value={s.supplier_score ?? 0} count={5} style={{ fontSize: 10, flexShrink: 0 }} />
    </div>
  );
}

function SupplierList({ list, checkedIds, onToggle, onToggleAll, selectedGroups, previouslyContactedIds }) {
  const [search, setSearch] = useState("");

  if (list.length === 0) {
    return <Empty description="No suppliers found" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "20px 0" }} />;
  }

  const q = search.trim().toLowerCase();
  const filtered = q
    ? list.filter(s => s.name?.toLowerCase().includes(q) || (s.number || "").includes(q))
    : list;

  const allChecked = filtered.length > 0 && filtered.every(s => checkedIds.has(s.id));
  const someChecked = filtered.some(s => checkedIds.has(s.id)) && !allChecked;
  const selectedInTab = list.filter(s => checkedIds.has(s.id)).length;

  // Group filtered suppliers by score (descending: 5→4→3→2→1→unrated)
  const groups = [];
  const seen = new Set();
  filtered.forEach(s => {
    const score = s.supplier_score ?? -1;
    if (!seen.has(score)) { seen.add(score); groups.push({ score, items: [] }); }
    groups.find(g => g.score === score).items.push(s);
  });
  groups.sort((a, b) => b.score - a.score);

  return (
    <>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "6px 0", borderBottom: "1px solid #f0f0f0", marginBottom: 6,
      }}>
        <Checkbox indeterminate={someChecked} checked={allChecked} onChange={() => onToggleAll(filtered, allChecked)}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>
            {allChecked ? "Deselect all" : "Select all"} ({filtered.length}{q ? `/${list.length}` : ""})
          </span>
        </Checkbox>
        <div style={{ flex: 1 }} />
        {selectedInTab > 0 && (
          <Tag color="blue" style={{ margin: 0, flexShrink: 0 }}>{selectedInTab} selected</Tag>
        )}
        <Input
          size="small"
          placeholder="Name or phone…"
          prefix={<SearchOutlined style={{ color: "#bfbfbf", fontSize: 11 }} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          allowClear
          style={{ width: 150, fontSize: 12 }}
        />
      </div>

      <div style={{ height: "calc(100vh - 460px)", minHeight: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
        {filtered.length === 0 && (
          <Empty description="No match" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "20px 0" }} />
        )}
        {groups.map(({ score, items }) => {
          const groupAllChecked = items.every(s => checkedIds.has(s.id));
          const groupSomeChecked = items.some(s => checkedIds.has(s.id)) && !groupAllChecked;
          return (
            <div key={score}>
              {/* Group header */}
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "4px 6px", marginBottom: 3, marginTop: 4,
                borderRadius: 4, background: "#f5f5f5",
              }}>
                {score >= 1
                  ? <Rate disabled value={score} count={5} style={{ fontSize: 11 }} />
                  : <span style={{ fontSize: 11, color: "#8c8c8c" }}>Unrated</span>
                }
                <span style={{ fontSize: 11, color: "#8c8c8c" }}>({items.length})</span>
                <div style={{ flex: 1 }} />
                <Checkbox
                  indeterminate={groupSomeChecked}
                  checked={groupAllChecked}
                  onChange={() => onToggleAll(items, groupAllChecked)}
                  style={{ fontSize: 11 }}
                >
                  <span style={{ fontSize: 11 }}>
                    {groupAllChecked ? "Deselect" : "Select"} {score >= 1 ? "all" : "unrated"}
                  </span>
                </Checkbox>
              </div>
              {/* Suppliers in this group */}
              <div style={{ display: "flex", flexDirection: "column", gap: 3, paddingLeft: 4 }}>
                {items.map(s => (
                  <SupplierRow
                    key={s.id}
                    s={s}
                    checkedIds={checkedIds}
                    onToggle={onToggle}
                    selectedGroups={selectedGroups}
                    previouslyContactedIds={previouslyContactedIds}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export function SendToSuppliersModal({ queryPk, task, open, onClose, onSent, onEditClick, userId, initialGroups, previouslyContactedIds }) {
  const headers = { accessToken: localStorage.getItem("accessToken") };

  const [fetchedTask, setFetchedTask] = useState(null);
  const [taskLoading, setTaskLoading] = useState(false);
  // Field selection from the composer — sent alongside the suppliers so the
  // backend builds the message from exactly what the user previewed
  const selectionRef = React.useRef(null);

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());

  const [isInternational, setIsInternational] = useState(false);
  const [sendAcknowledgement, setSendAcknowledgement] = useState(true);

  // Which of the task's photos to actually attach to this message — defaults
  // to "all" (matching the previous no-selection behavior) whenever a new
  // task's images arrive, so switching between tasks/resends doesn't carry
  // over a stale selection from whatever was previously open.
  const [selectedImageIndices, setSelectedImageIndices] = useState(new Set());
  const imagesKey = (fetchedTask ?? task)?.images?.length ?? 0;
  useEffect(() => {
    const imgs = (fetchedTask ?? task)?.images;
    setSelectedImageIndices(new Set(Array.isArray(imgs) ? imgs.map((_, i) => i) : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, imagesKey]);
  const toggleImageIndex = (i) => setSelectedImageIndices(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

  // Stable identifier for an image entry — today's stored entries are plain
  // path strings, matching send_to_suppliers_v2's own getImageIdentifier
  // fallback; the object branch is future-proofing for if entries ever gain
  // a real shape.
  const imageIdentifier = (img) => (img != null && typeof img === "object")
    ? String(img.id ?? img.path ?? img.url ?? img.filePath ?? img.file_name ?? img.fileName ?? "")
    : String(img);

  // Passed to the composer so its live preview (and the photo-count text it
  // bakes in) reflects the current checkbox state instead of always
  // describing every photo — undefined when everything's selected, matching
  // the backend's own "omitted = send all" default rather than enumerating
  // every id for the common case.
  const imageIds = useMemo(() => {
    const imgs = (fetchedTask ?? task)?.images;
    if (!Array.isArray(imgs) || selectedImageIndices.size >= imgs.length) return undefined;
    return imgs.filter((_, i) => selectedImageIndices.has(i)).map(imageIdentifier);
  }, [fetchedTask, task, selectedImageIndices]);

  // Fetch full task details if task prop doesn't carry images (opened from a card)
  useEffect(() => {
    if (!open || !queryPk) { setFetchedTask(null); return; }
    // Always fetch — card prop may have stale/missing query_parts or images
    setTaskLoading(true);
    axios
      .get(`${API_DOMAIN}/query_workflow/task/get?query_pk=${queryPk}`, { headers })
      .then(res => {
        if (res.data.success) {
          const t = res.data.data?.task ?? null;
          const parts = res.data.data?.parts ?? t?.query_parts ?? [];
          setFetchedTask(t ? { ...t, query_parts: parts } : null);
        }
      })
      .catch(() => {})
      .finally(() => setTaskLoading(false));
  }, [open, queryPk]); // eslint-disable-line

  const validationTask = fetchedTask ?? task;

  // Owns all vehicle/parts selection state + the live server-side preview —
  // rendered across two separate columns below (SupplierSelectionPanel /
  // SupplierPreviewPanel) instead of one combined component, so the layout
  // can put "what to include" and "what it looks like" in their own panes.
  const composer = useSupplierMessageComposer({
    open,
    taskId: queryPk,
    task: validationTask,
    isInternational,
    imageIds,
    onSelectionChange: sel => { selectionRef.current = sel; },
  });

  // Auto-set international flag based on customer phone when task data arrives
  useEffect(() => {
    if (!validationTask) return;
    const phone = (validationTask.customer?.customer_mobile || "").replace(/^\+/, "");
    setIsInternational(!!phone && !phone.startsWith("971"));
  }, [validationTask]); // eslint-disable-line

  // Pre-populate groups from previous send attempts when opening for a resend
  useEffect(() => {
    if (open && initialGroups?.length > 0) setSelectedGroups(initialGroups);
  }, [open]); // eslint-disable-line

  const missing = [];
  if (!validationTask?.vehicle && !validationTask?.vehicle_id) missing.push("vehicle");
  if (!validationTask?.query_parts?.length) missing.push("parts");
  if (!validationTask?.images?.length) missing.push("images");
  // Deselecting every photo isn't "send with no photos" — send_to_suppliers_v2
  // hard-errors when a non-empty image_ids selection matches zero images
  // (there's no server-side way to represent "send zero images"), so this
  // has to be blocked client-side rather than let that request fail.
  const noImagesSelected = (validationTask?.images?.length ?? 0) > 0 && selectedImageIndices.size === 0;
  const canSend = missing.length === 0 && !noImagesSelected && !taskLoading;

  // Load supplier groups once — cached at module level across all modal instances
  useEffect(() => {
    if (_cachedGroups) { setGroups(_cachedGroups); return; }
    setGroupsLoading(true);
    fetchGroupsOnce().then(g => setGroups(g)).finally(() => setGroupsLoading(false));
  }, []); // eslint-disable-line

  // Load suppliers when groups change — auto-select uzdpart suppliers
  useEffect(() => {
    if (!open || selectedGroups.length === 0) {
      setSuppliers([]);
      setCheckedIds(new Set());
      return;
    }
    setSuppliersLoading(true);
    setCheckedIds(new Set());
    axios
      .post(`${API_DOMAIN}/whatsapp/get_suppliers`, { make: selectedGroups }, { headers })
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        const sorted = [...raw].sort((a, b) => (b.supplier_score ?? 0) - (a.supplier_score ?? 0));
        setSuppliers(sorted);
        const autoSelected = sorted
          .filter(s => s.name?.toLowerCase().includes("uzdpart"))
          .map(s => s.id);
        setCheckedIds(new Set(autoSelected));
      })
      .catch(() => {})
      .finally(() => setSuppliersLoading(false));
  }, [selectedGroups, open]); // eslint-disable-line

  const toggleSupplier = (id) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllInList = (list, allChecked) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (allChecked) list.forEach(s => next.delete(s.id));
      else list.forEach(s => next.add(s.id));
      return next;
    });
  };

  const handleSend = () => {
    if (!checkedIds.size) return;
    if (!canSend) return;
    const checkedSuppliers = suppliers.filter(s => checkedIds.has(s.id)).map(s => ({
      supplier_id: s.id,
      supplier_name: s.name,
      supplier_number: s.number,
      supplier_score: s.supplier_score ?? null,
    }));
    const t = validationTask;
    const v = t?.vehicle ?? {};
    const allImages = Array.isArray(t?.images) ? t.images : [];
    // Legacy path has no selection concept server-side, so the filtering
    // happens client-side here — it just sends whatever's in `images`.
    const selectedImages = allImages.filter((_, i) => selectedImageIndices.has(i));
    const queryData = {
      brand: v.brand_name || v.brand || "",
      model: v.model_name || v.model || "",
      year: v.year || null,
      region: v.region || "",
      parts: (t?.query_parts || []).map(p => p.part_name).filter(Boolean),
      images: selectedImages,
      remarks: t?.remarks || "",
    };
    // v2: selectionRef.current already carries image_ids — it's built by
    // the composer from the same `imageIds` prop this component passes it
    // (see buildSelection in SupplierMessageComposer.js), so the live
    // preview and the actual send are guaranteed to agree on which photos
    // go out instead of two independently-derived selections drifting.
    // Legacy path kept as fallback if the composer never produced a
    // selection (shouldn't happen once the task loads).
    const selection = selectionRef.current;
    const payload = selection
      ? {
          task_id: queryPk,
          userId,
          is_international: isInternational,
          send_acknowledgement: sendAcknowledgement,
          selected_groups: selectedGroups,
          suppliers: checkedSuppliers,
          selection,
        }
      : {
          task_id: queryPk,
          userId,
          is_international: isInternational,
          send_acknowledgement: sendAcknowledgement,
          selected_groups: selectedGroups,
          suppliers: checkedSuppliers,
          query_data: queryData,
        };
    const endpoint = selection
      ? `${API_DOMAIN}/query_workflow/send_to_suppliers_v2`
      : `${API_DOMAIN}/query_workflow/send_to_suppliers`;
    onSent?.(checkedSuppliers.length);
    handleClose();
    axios.post(endpoint, payload, { headers }).catch(() => {});
  };

  const handleClose = () => {
    setSelectedGroups([]);
    setSuppliers([]);
    setCheckedIds(new Set());
    setFetchedTask(null);
    setIsInternational(false);
    setSendAcknowledgement(true);
    onClose();
  };

  const uzdSuppliers = suppliers.filter(s => s.name?.toLowerCase().includes("uzdpart"));
  const otherSuppliers = suppliers.filter(s => !s.name?.toLowerCase().includes("uzdpart"));
  const displayTask = task ?? fetchedTask;

  const tabItems = [
    {
      key: "others",
      label: `Suppliers${otherSuppliers.length ? ` (${otherSuppliers.length})` : ""}`,
      children: (
        <SupplierList
          list={otherSuppliers}
          checkedIds={checkedIds}
          onToggle={toggleSupplier}
          onToggleAll={toggleAllInList}
          selectedGroups={selectedGroups}
          previouslyContactedIds={previouslyContactedIds}
        />
      ),
    },
    {
      key: "uzd",
      label: (
        <span>
          UZD Part{uzdSuppliers.length ? ` (${uzdSuppliers.length})` : ""}
          {uzdSuppliers.some(s => checkedIds.has(s.id)) && (
            <Tag color="blue" style={{ marginLeft: 6, fontSize: 10, padding: "0 4px", lineHeight: "16px" }}>
              {uzdSuppliers.filter(s => checkedIds.has(s.id)).length} selected
            </Tag>
          )}
        </span>
      ),
      children: (
        <SupplierList
          list={uzdSuppliers}
          checkedIds={checkedIds}
          onToggle={toggleSupplier}
          onToggleAll={toggleAllInList}
          selectedGroups={selectedGroups}
          previouslyContactedIds={previouslyContactedIds}
        />
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={`Send to Suppliers${displayTask?.query_id ? ` — #${displayTask.query_id}` : ""}`}
      width={1320}
      style={{ top: 16 }}
      zIndex={1050}
      styles={{ body: { height: "calc(100vh - 172px)", overflow: "hidden", display: "flex", gap: 20 } }}
      footer={[
        <Button key="cancel" onClick={handleClose}>Cancel</Button>,
        <Button
          key="send"
          type="primary"
          icon={<SendOutlined />}
          disabled={!checkedIds.size || !canSend}
          onClick={handleSend}
        >
          Send to {checkedIds.size || ""} supplier{checkedIds.size !== 1 ? "s" : ""}
        </Button>,
      ]}
    >
      {/* ── Col 1: supplier group + list ── */}
      <div style={{ flex: "0 0 300px", minWidth: 0, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {taskLoading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <Spin size="small" />
          </div>
        )}

        {!taskLoading && canSend && (
          <>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#595959" }}>
                Supplier group
              </div>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder={groupsLoading ? "Loading groups…" : "Select a supplier group"}
                loading={groupsLoading}
                options={groups}
                value={selectedGroups}
                onChange={setSelectedGroups}
                optionFilterProp="label"
                allowClear
                maxTagCount="responsive"
              />
            </div>

            {selectedGroups.length === 0 ? (
              <Empty
                description="Select a supplier group above to load suppliers"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: "16px 0" }}
              />
            ) : suppliersLoading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                <Spin />
              </div>
            ) : (
              <Tabs
                size="small"
                defaultActiveKey="others"
                style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
                tabBarStyle={{ marginBottom: 8 }}
                items={tabItems}
              />
            )}
          </>
        )}
      </div>

      {/* ── Col 2: query details + what to include ── */}
      {/* No overflow here — only the Parts list inside SupplierSelectionPanel
          scrolls on its own; the banner/alerts/toggles above it stay fixed. */}
      <div style={{
        flex: 1, minWidth: 0, minHeight: 0,
        borderLeft: "1px solid #f0f0f0", paddingLeft: 20,
        overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        {!taskLoading && displayTask && (
          <QueryDetailsBanner task={displayTask} selectedIndices={selectedImageIndices} onToggle={toggleImageIndex} />
        )}

        {!taskLoading && missing.length > 0 && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 14, fontSize: 12 }}
            message="This query can't be sent yet"
            description={
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <span>
                  Missing:{" "}
                  {missing.map(m => (
                    <Tag key={m} color="red" style={{ margin: "2px 2px" }}>{m}</Tag>
                  ))}
                </span>
                {onEditClick && (
                  <Button size="small" onClick={() => { onClose(); onEditClick(); }}>
                    Edit Draft
                  </Button>
                )}
              </div>
            }
          />
        )}

        {!taskLoading && missing.length === 0 && noImagesSelected && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 14, fontSize: 12 }}
            message="No photos selected"
            description="Select at least one photo above to include in this message, or check them all to send everything."
          />
        )}

        {!taskLoading && canSend && (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {[
                {
                  checked: sendAcknowledgement,
                  onChange: e => setSendAcknowledgement(e.target.checked),
                  icon: <MessageOutlined style={{ fontSize: 13, color: sendAcknowledgement ? "#52c41a" : "#8c8c8c" }} />,
                  label: "Acknowledge customer",
                  tooltip: "Sends the customer a WhatsApp message confirming their query has been received and is being sourced",
                  activeColor: "#389e0d", activeBg: "#f6ffed", activeBorder: "#b7eb8f",
                },
                {
                  checked: isInternational,
                  onChange: e => setIsInternational(e.target.checked),
                  icon: <GlobalOutlined style={{ fontSize: 13, color: isInternational ? "#faad14" : "#8c8c8c" }} />,
                  label: "International order",
                  tooltip: "Notifies suppliers that this is an international order — they will factor in cross-border shipping and customs in their quotes",
                  activeColor: "#d48806", activeBg: "#fffbe6", activeBorder: "#faad14",
                },
              ].map(({ checked, onChange, icon, label, tooltip, activeColor, activeBg, activeBorder }) => (
                <Tooltip key={label} title={tooltip} placement="bottom">
                  <div
                    onClick={() => onChange({ target: { checked: !checked } })}
                    style={{
                      flex: 1, display: "flex", alignItems: "center", gap: 8,
                      background: checked ? activeBg : "#fafafa",
                      border: `1px solid ${checked ? activeBorder : "#f0f0f0"}`,
                      borderRadius: 8, padding: "8px 10px", cursor: "pointer",
                      transition: "all 0.2s", userSelect: "none",
                    }}
                  >
                    {icon}
                    <span style={{ fontSize: 12, fontWeight: 600, color: checked ? activeColor : "#595959", flex: 1 }}>
                      {label}
                    </span>
                    <Checkbox checked={checked} onChange={onChange} onClick={e => e.stopPropagation()} />
                  </div>
                </Tooltip>
              ))}
            </div>

            <SupplierSelectionPanel composer={composer} />
          </>
        )}
      </div>

      {/* ── Col 3: supplier's view ── */}
      <div style={{
        flex: "0 0 380px", minWidth: 0,
        borderLeft: "1px solid #f0f0f0", paddingLeft: 20,
        display: "flex", flexDirection: "column",
      }}>
        {!taskLoading && canSend ? (
          <SupplierPreviewPanel composer={composer} />
        ) : (
          <div style={{ margin: "auto", color: "#bfbfbf", fontSize: 12 }}>
            {taskLoading ? "Loading query…" : "Complete the query to compose the message"}
          </div>
        )}
      </div>
    </Modal>
  );
}
